import React from 'react';
import styled from 'styled-components';
import { useTrackWidget } from 'shared/util/analytics/useTrackWidget';
import {
  previewSelectedFilters,
  RepeaterSidebarPlugin,
} from './RepeaterSidebarPlugin';
import { Config } from './types';
import RepeaterPagination from './RepeaterPagination';

const StyledRepeaterWidget = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`;

/**
 * RepeaterWidget
 * Merged resolution of conflicting branches:
 *  - Tracking instrumentation (enableTracking branch)
 *  - Filtering + pagination preview logic (master branch)
 *
 * Adds robust filter change tracking while preserving previewSelectedFilters initialization.
 * Skips analytics when editing mode is active.
 */
const RepeaterWidget: React.FC<WidgetRenderProps<Config>> = ({
  widgetState,
  currentPage,
  siteId,
  updateCurrentPage,
  editing,
  site,
  tracking,
}) => {
  const config = widgetState.config;
  const {
    borderData,
    backgroundData,
    backgroundImage,
    lineColor,
    visible,
    isCustomWidget,
  } = config;

  const trackRepeater = useTrackWidget({
    tracking,
    widgetState,
    currentPage,
    site,
  });

  if (visible === false || isCustomWidget) {
    return <></>; // Widget hidden or custom placeholder
  }

  // Initialize preview selected filters for preview mode - start empty for proper filtering
  React.useEffect(() => {
    if (
      config.enableFiltering &&
      config.filterBy &&
      config.repeaterData?.data
    ) {
      if (!previewSelectedFilters[widgetState.id]) {
        previewSelectedFilters[widgetState.id] = [];
      }
    }
  }, [
    widgetState.id,
    config.enableFiltering,
    config.filterBy,
    config.repeaterData?.data,
  ]);

  // Precompute style values for readability (replaces nested ternaries flagged by lint)
  const rawBgColor = backgroundData?.color;
  const isHexOrRgb =
    rawBgColor?.startsWith('#') || rawBgColor?.startsWith('rgb');
  const useImage = backgroundData?.style === 'Image';
  const useColorPattern = backgroundData?.style === 'Color' && !isHexOrRgb;

  const widgetStyles: React.CSSProperties = {
    backgroundColor: isHexOrRgb ? rawBgColor : '',
    backgroundImage: (() => {
      if (useImage) return `url('${backgroundImage?.url}')`;
      if (useColorPattern) return rawBgColor || '';
      return '';
    })(),
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    borderWidth: `${borderData?.thickness}px`,
    borderStyle: 'solid',
    borderColor: `${lineColor}`,
    borderRadius: `${borderData?.radius}px`,
    backgroundClip: 'content-box',
  };

  // Pagination offset (px) moves to the right when filtering sidebar is present in view mode
  let paginationLeftPx = 0;
  if (!editing && config.enableFiltering) {
    paginationLeftPx = 200;
  }

  return (
    <StyledRepeaterWidget
      id="repeaterWidget"
      className="repeaterWidget"
      style={widgetStyles}
    >
      {config.enableFiltering && config.filterBy && (
        <div
          style={{
            position: 'absolute',
            left: editing ? '-200px' : '0px',
            top: 0,
            height: '100%',
          }}
        >
          <RepeaterSidebarPlugin
            widget={widgetState}
            currentPage={currentPage}
            siteId={siteId}
            updateCurrentPage={updateCurrentPage}
            data-test-id="repeater-sidebar-plugin"
            onFilterChange={(filterValue: string) => {
              if (editing) return; // Avoid tracking during edit operations
              trackRepeater('repeater_widget_filter_change', {
                filterBy: widgetState.config.filterBy,
                filterValue,
                widgetAnalyticsLabel: widgetState.config.analyticsLabel,
              });
            }}
          />
        </div>
      )}

      {/* Containers rendered automatically; Sidebar handled by WidgetConnectorNew */}

      {config.enablePagination && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: paginationLeftPx,
            width: '100%',
            zIndex: 1001,
          }}
        >
          <RepeaterPagination
            currentPage={currentPage}
            widgetState={widgetState}
            siteId={siteId}
          />
        </div>
      )}
    </StyledRepeaterWidget>
  );
};

export default RepeaterWidget;
